/**
 * usePlacementQna — TanStack Query hooks for the Ausschreibungs-Screen Tab 3c (Rückfragen / Q&A,
 * Prozessschritt 8; P2 Placement Slice 9).
 *
 * Server state = TanStack Query ONLY. live mode hits apiClient.placements.*Qna; mock mode resolves the
 * Ü3c fixture (AXA SUB-2026-000482-HAFT offen with one unread question · Zurich …483 erledigt · Helvetia
 * …484 no thread). The server owns every decision; the UI only triggers + renders. The mutations
 * invalidate the qna key (and, for antworten, the submissions key - cheap, the Verlauf may show it later).
 *
 * [Source: apps/web/src/features/placements/usePlacementSubmissions.ts] (live/mock + qk + invalidate idiom)
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../lib/api-client';
import { qk } from '../../lib/query-keys';
import { isLiveData } from '../../lib/data-mode';
import {
  isQnaUeberfaellig,
  qnaErlaubteTransitionen,
  qnaVerfasserOf,
  qnaZaehler,
  QNA_THREAD_STATUS_LABELS,
} from '@brokly/shared';
import type {
  QnaAntwortenBody,
  QnaErledigtBody,
  QnaGelesenResponse,
  QnaListResponse,
  QnaMessageEintrag,
  QnaSubmissionEintrag,
  QnaThreadEintrag,
} from '@brokly/shared';

// ── Ü3c mock fixture (mock mode only) ─────────────────────────────────────────────

const TODAY = '2026-10-05';

function msg(over: Partial<QnaMessageEintrag> & { id: string; direction: QnaMessageEintrag['direction'] }): QnaMessageEintrag {
  return {
    channel: 'email',
    communication_id: `comm-${over.id}`,
    email_message_id: `<${over.id}@mail>`,
    betreff: 'Re: Offertanfrage',
    text: '',
    absender: null,
    empfaenger: null,
    attachment_count: 0,
    match_art: over.direction === 'versicherer_an_broker' ? 'thread' : null,
    absender_abweichend: false,
    gelesen: true,
    gelesen_at: null,
    bounce_at: null,
    bounce_grund: null,
    verfasser: null,
    created_by: null,
    created_at: '2026-10-05T00:00:00.000Z',
    ...over,
  };
}

function thread(status: QnaThreadEintrag['status'], over: Partial<QnaThreadEintrag> & { id: string }): QnaThreadEintrag {
  return {
    status,
    status_label: QNA_THREAD_STATUS_LABELS[status],
    status_changed_at: '2026-10-05T09:12:00.000Z',
    offen_seit: status === 'offen' ? '2026-10-05T09:12:00.000Z' : null,
    antwort_frist_date: status === 'offen' ? '2026-10-08' : null,
    ueberfaellig: false,
    frist_eskaliert_at: null,
    frist_task_id: null,
    frage_task_id: null,
    letzte_frage_at: '2026-10-05T09:12:00.000Z',
    letzte_antwort_at: null,
    anzahl_fragen: 1,
    anzahl_antworten: 0,
    ungelesen: 0,
    erledigt_at: status === 'erledigt' ? '2026-10-03T14:40:00.000Z' : null,
    erledigt_by: null,
    erledigt_by_name: null,
    erledigt_art: status === 'erledigt' ? 'antwort' : null,
    erledigt_grund: null,
    versand_laeuft: false,
    letzter_versandfehler: null,
    letzter_versandfehler_at: null,
    erlaubte_transitionen: qnaErlaubteTransitionen(status),
    antwort_empfaenger: null,
    antwort_empfaenger_abweichend: false,
    antwort_empfaenger_quelle: null,
    messages: [],
    created_at: '2026-10-05T09:12:00.000Z',
    updated_at: '2026-10-05T09:12:00.000Z',
    ...over,
  };
}

function mockQna(id: string): QnaListResponse {
  const axaThread = thread('offen', {
    id: 'th-axa',
    antwort_frist_date: '2026-10-08',
    antwort_empfaenger: 'underwriting@axa.ch',
    antwort_empfaenger_abweichend: false,
    antwort_empfaenger_quelle: 'letzte_eingehende',
    ungelesen: 1,
    ueberfaellig: isQnaUeberfaellig('offen', '2026-10-08', TODAY),
    messages: [
      msg({
        id: 'm-axa-1',
        direction: 'versicherer_an_broker',
        absender: 'underwriting@axa.ch',
        verfasser: qnaVerfasserOf('underwriting@axa.ch'),
        betreff: 'Re: Offertanfrage SUB-2026-000482-HAFT',
        text: 'Bitte um Angabe der aktuellen Mitarbeiterzahl und der Lohnsumme 2025.',
        gelesen: false,
        gelesen_at: null,
        created_at: '2026-10-05T09:12:00.000Z',
      }),
    ],
  });
  const zurThread = thread('erledigt', {
    id: 'th-zur',
    anzahl_antworten: 1,
    letzte_antwort_at: '2026-10-03T14:40:00.000Z',
    messages: [
      msg({ id: 'm-zur-1', direction: 'versicherer_an_broker', absender: 'uw@zurich.ch', verfasser: qnaVerfasserOf('uw@zurich.ch'), text: 'Frage zur Soll-Deckung.', created_at: '2026-10-03T11:00:00.000Z' }),
      msg({ id: 'm-zur-2', direction: 'broker_an_versicherer', verfasser: 'Sie', text: 'Antwort folgt anbei.', created_at: '2026-10-03T14:40:00.000Z' }),
    ],
  });

  const items: QnaSubmissionEintrag[] = [
    { submission_id: 'sub-axa', submission_number: 'SUB-2026-000482-HAFT', carrier_uid: 'CHE-AXA', name: 'AXA', logo_url: null, submission_status: 'versandt', carrier_email_to: 'offerten@axa.ch', carrier_domain: 'axa.ch', antwort_moeglich: true, thread: axaThread },
    { submission_id: 'sub-zur', submission_number: 'SUB-2026-000483-HAFT', carrier_uid: 'CHE-ZUR', name: 'Zurich', logo_url: null, submission_status: 'eingegangen', carrier_email_to: 'offerten@zurich.ch', carrier_domain: 'zurich.ch', antwort_moeglich: true, thread: zurThread },
    { submission_id: 'sub-hel', submission_number: 'SUB-2026-000484-HAFT', carrier_uid: 'CHE-HEL', name: 'Helvetia', logo_url: null, submission_status: 'versandt', carrier_email_to: 'offerten@helvetia.ch', carrier_domain: 'helvetia.ch', antwort_moeglich: true, thread: null },
  ];
  return {
    placement_id: id,
    placement_number: 'PL-2026-000001',
    status: 'ausgeschrieben',
    aktion_erlaubt: true,
    sperrgrund: null,
    mailbox_verbunden: true,
    ...qnaZaehler(items),
    frist_default_date: '2026-10-08',
    items,
  };
}

// ── Query ──────────────────────────────────────────────────────────────────────

export function usePlacementQna(id: string) {
  const live = isLiveData();
  return useQuery<QnaListResponse>({
    queryKey: qk.placementQna(id, live ? 'live' : 'mock'),
    queryFn: () => (live ? apiClient.placements.getQna(id) : Promise.resolve(mockQna(id))),
    ...(live ? {} : { initialData: () => mockQna(id), staleTime: Infinity }),
  });
}

// ── Mutations ──────────────────────────────────────────────────────────────────

function useQnaInvalidate(id: string, alsoSubmissions = false) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['placements', 'qna', id] });
    if (alsoSubmissions) void qc.invalidateQueries({ queryKey: ['placements', 'submissions', id] });
  };
}

export function useAntwortenQna(id: string) {
  const live = isLiveData();
  const invalidate = useQnaInvalidate(id, true);
  const invalidateQna = useQnaInvalidate(id);
  return useMutation({
    mutationFn: ({ sid, body }: { sid: string; body: QnaAntwortenBody }) =>
      live ? apiClient.placements.antwortenQna(id, sid, body) : Promise.resolve(mockQna(id)),
    onSuccess: invalidate,
    // A refused send means the cached thread is stale (e.g. a new off-domain recipient to confirm).
    onError: invalidateQna,
  });
}

export function useErledigtQna(id: string) {
  const live = isLiveData();
  const invalidate = useQnaInvalidate(id);
  return useMutation({
    mutationFn: ({ sid, body }: { sid: string; body: QnaErledigtBody }) =>
      live ? apiClient.placements.erledigtQna(id, sid, body) : Promise.resolve(mockQna(id)),
    onSuccess: invalidate,
  });
}

export function useOffenQna(id: string) {
  const live = isLiveData();
  const invalidate = useQnaInvalidate(id);
  return useMutation({
    mutationFn: ({ sid }: { sid: string }) =>
      live ? apiClient.placements.offenQna(id, sid) : Promise.resolve(mockQna(id)),
    onSuccess: invalidate,
  });
}

export function usePatchQnaFrist(id: string) {
  const live = isLiveData();
  const invalidate = useQnaInvalidate(id);
  return useMutation({
    mutationFn: ({ sid, antwort_frist_date }: { sid: string; antwort_frist_date: string }) =>
      live ? apiClient.placements.patchQnaFrist(id, sid, { antwort_frist_date }) : Promise.resolve(mockQna(id)),
    onSuccess: invalidate,
  });
}

export function useGelesenQna(id: string) {
  const live = isLiveData();
  const invalidate = useQnaInvalidate(id);
  return useMutation({
    mutationFn: ({ sid }: { sid: string }): Promise<QnaGelesenResponse> =>
      live ? apiClient.placements.gelesenQna(id, sid) : Promise.resolve({ ...mockQna(id), markiert: 0 }),
    onSuccess: invalidate,
  });
}
